import { useIntl } from '@/intl'
import { Price } from '@/scopes/common/domain/acco-lister/types'
import { useAccoPriceDetails } from '@/scopes/products/pages/components/RecommendedAccos/hooks'
import { Icon, IndeterminateProgressCircle, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import styles from './PriceDetails.module.css'

interface AccoPriceDetailsProps {
  accommodationId: number
  onClose: () => void
  price: Price
}

export function AccoPriceDetails({ onClose, price, accommodationId }: AccoPriceDetailsProps) {
  const intl = useIntl()
  const { error, isLoading, priceDetails } = useAccoPriceDetails({
    accommodationId,
    departureDate: price.departureDate,
    duration: price.durationInDays,
    mealPlan: price.mealplan?.code ?? '',
    transportType: price.transportType,
    participants: price.participants,
  })

  return (
    // The `open` is always true because the modal is conditionally rendered
    // by its parent to avoid making an API request until the user clicks
    // on the price details button
    <Sheet.Root open onOpenChange={onClose}>
      <Sheet.Body side="right">
        <Sheet.Header closeLabel={intl.formatMessage({ id: 'close' })} titleLabel={priceDetails?.title ?? ''} />
        <Sheet.Content>
          {isLoading && <IndeterminateProgressCircle />}
          {error && (
            <div className={styles.errorMessage}>
              <Icon glyph="ExclamationInTriangle" size={32} />
              <Typography forwardedAs="p" variant="body-l-bold-tight">
                {intl.formatMessage({ id: 'price_details_error_msg' })}
              </Typography>
            </div>
          )}
          {priceDetails && (
            <Typography
              className={styles.priceDetails}
              forwardedAs="div"
              variant="body-m-regular-tight"
              dangerouslySetInnerHTML={{ __html: priceDetails.content }}
            />
          )}
        </Sheet.Content>
      </Sheet.Body>
    </Sheet.Root>
  )
}
